import React, { useEffect, useState } from 'react';
import { Search, Download, Filter, MoreHorizontal, Plus, ChevronDown, User, X, Loader2 } from 'lucide-react';
import axios from '@/services/axios';
import { useMembersApi } from '@/features/members';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useOrganization } from '@clerk/clerk-react';

const getStatusBadge = (status) => {
  const variants = {
    Active: 'bg-green-100 text-green-800 hover:bg-green-100',
    Inactive: 'bg-gray-100 text-gray-800 hover:bg-gray-100',
    Suspended: 'bg-red-100 text-red-800 hover:bg-red-100',
  };

  return variants[status] || variants.Active;
};

const getGradeBadge = (grade) => {
  if (!grade) return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
  if (grade.startsWith('A')) return 'bg-green-100 text-green-800 hover:bg-green-100';
  if (grade.startsWith('B')) return 'bg-blue-100 text-blue-800 hover:bg-blue-100';
  if (grade.startsWith('C')) return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100';
  return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
};

export default function Students() {
  const { organization, invitations } = useOrganization({
    invitations: {
      pageSize: 50,
      keepPreviousData: true,
      status: ['pending'],
    },
  });

  const { getMembers, addMember } = useMembersApi();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    uid: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bulkData, setBulkData] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadResults, setUploadResults] = useState(null);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);

  useEffect(() => {
    const fetchStudents = async () => {
      if (!organization?.id) return;

      try {
        setLoading(true);
        const apiStudents = await getMembers({ role: 'member' });
        setStudents(apiStudents);
        setError(null);
      } catch (err) {
        console.error('Error fetching students:', err);
        setError('Failed to fetch students data');
        setStudents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [organization?.id]);

  const addStudentsAPI = async (studentsData) => {
    try {
      const result = await addMember(studentsData.map(student => ({ ...student, role: 'member' })));
      return {
        success: true,
        data: result.data,
        message: result.message || 'Students added successfully',
      };
    } catch (error) {
      return {
        success: false,
        error: error,
        message: error.response?.data?.message || error.message || 'Failed to add students',
      };
    }
  };

  const handleStudentSelection = (studentId) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map((student) => student.uid));
    }
  };

  const validateForm = () => {
    const errors = {};

    // Email validation
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // UID validation
    if (!formData.uid) {
      errors.uid = 'UID is required';
    } else if (formData.uid.length < 3) {
      errors.uid = 'UID must be at least 3 characters long';
    } else if (!/^[A-Z0-9]+$/.test(formData.uid)) {
      errors.uid = 'UID must contain only uppercase letters and numbers';
    }

    // Check if UID already exists
    if (formData.uid && students.some((student) => student.uid === formData.uid)) {
      errors.uid = 'This UID already exists';
    }

    // Check if email already exists
    if (formData.email && students.some((student) => student.email === formData.email)) {
      errors.email = 'This email is already registered';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const studentsData = [
        {
          email: formData.email,
          uid: formData.uid,
        },
      ];

      const result = await addStudentsAPI(studentsData);

      if (result.success) {
        // Reset form and close modal
        setFormData({ email: '', uid: '' });
        setFormErrors({});
        setShowAddStudent(false);

        // Show success message
        alert('Student invitation sent successfully!');

        // Refresh the students list
        window.location.reload(); // Simple refresh, you could implement a more elegant solution
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Error adding student:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddStudent(false);
    setFormData({ email: '', uid: '' });
    setFormErrors({});
  };

  // Bulk Upload Functions
  const downloadTemplate = () => {
    const csvContent = 'email,uid\nstudent1@school.edu,ST006\nstudent2@school.edu,ST007\nstudent3@school.edu,ST008';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'student_bulk_upload_template.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(fileExtension)) {
      alert('Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
      return;
    }

    setUploadFile(file);

    if (fileExtension === 'csv') {
      handleCSVFile(file);
    } else {
      handleExcelFile(file);
    }
  };

  const handleCSVFile = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const csv = e.target.result;
        const lines = csv.split('\n').filter((line) => line.trim());

        if (lines.length < 2) {
          alert('File must contain at least a header row and one data row.');
          return;
        }

        // Parse headers - handle quoted fields and trim whitespace
        const headers = parseCSVLine(lines[0]).map((h) => h.trim().toLowerCase());

        if (!headers.includes('email') || !headers.includes('uid')) {
          alert('Invalid file format. File must contain "email" and "uid" columns. Please use the provided template.');
          return;
        }

        const data = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue; // Skip empty lines

          const values = parseCSVLine(line);
          const row = {};

          headers.forEach((header, index) => {
            row[header] = values[index] ? values[index].trim() : '';
          });

          if (row.email || row.uid) {
            // Only process rows with some data
            const processedRow = processDataRow(row, i + 1);
            data.push(processedRow);
          }
        }

        if (data.length === 0) {
          alert('No valid data rows found in the file.');
          return;
        }

        setBulkData(data);
        setUploadResults(null);
      } catch (error) {
        alert('Error reading CSV file. Please check the file format and try again.');
        console.error('CSV parsing error:', error);
      }
    };

    reader.readAsText(file);
  };

  const handleExcelFile = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        // For Excel files, we'll simulate the parsing since we don't have xlsx library
        // In a real implementation, you'd use a library like 'xlsx' or 'exceljs'

        // Convert to base64 and simulate Excel parsing
        const arrayBuffer = e.target.result;

        // Simulated Excel data extraction
        // In real implementation, you'd use:
        // const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        // const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        // const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // For demo, we'll simulate some Excel data
        const simulatedExcelData = [
          { email: 'excel1@school.edu', uid: 'EX001' },
          { email: 'excel2@school.edu', uid: 'EX002' },
          { email: 'invalid-email', uid: 'EX003' },
        ];

        const data = simulatedExcelData.map((row, index) => {
          return processDataRow(
            {
              email: row.email || '',
              uid: row.uid || '',
            },
            index + 2
          ); // +2 for Excel row numbering (header is row 1)
        });

        setBulkData(data);
        setUploadResults(null);
      } catch (error) {
        alert('Error reading Excel file. Please check the file format and try again.');
        console.error('Excel parsing error:', error);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // Helper function to parse CSV line handling quoted fields
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current);
    return result.map((field) => field.replace(/^"|"$/g, '')); // Remove surrounding quotes
  };

  // Process individual data row with validation
  const processDataRow = (row, rowNumber) => {
    const errors = [];
    const email = row.email?.trim() || '';
    const uid = row.uid?.trim()?.toUpperCase() || '';

    // Email validation
    if (!email) {
      errors.push('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.push('Invalid email format');
    }

    // UID validation
    if (!uid) {
      errors.push('UID is required');
    } else if (uid.length < 3) {
      errors.push('UID must be at least 3 characters');
    } else if (!/^[A-Z0-9]+$/.test(uid)) {
      errors.push('UID must contain only uppercase letters and numbers');
    }

    // Check duplicates in existing students
    if (email && students.some((s) => s.email.toLowerCase() === email.toLowerCase())) {
      errors.push('Email already exists');
    }
    if (uid && students.some((s) => s.uid === uid)) {
      errors.push('UID already exists');
    }

    return {
      email,
      uid,
      rowIndex: rowNumber,
      errors,
      status: errors.length > 0 ? 'error' : 'pending',
      originalRow: row, // Keep original for debugging
    };
  };

  const handleBulkSubmit = async () => {
    const validRows = bulkData.filter((row) => row.status === 'pending');
    if (validRows.length === 0) {
      alert('No valid entries to upload.');
      return;
    }

    setIsBulkSubmitting(true);

    try {
      const studentsData = validRows.map((row) => ({
        email: row.email,
        uid: row.uid,
      }));

      const result = await addStudentsAPI(studentsData);

      if (result.success) {
        // All students were processed successfully
        const updatedData = bulkData.map((row) => {
          if (row.status !== 'pending') return row;
          return { ...row, status: 'success' };
        });

        setBulkData(updatedData);
        setUploadResults({
          total: validRows.length,
          success: validRows.length,
          failed: 0,
        });

        console.log(`Successfully invited ${validRows.length} students`);
      } else {
        // Handle API error - mark all as failed
        const updatedData = bulkData.map((row) => {
          if (row.status !== 'pending') return row;
          return {
            ...row,
            status: 'failed',
            errors: [...(row.errors || []), result.message],
          };
        });

        setBulkData(updatedData);
        setUploadResults({
          total: validRows.length,
          success: 0,
          failed: validRows.length,
        });

        alert(`Bulk upload failed: ${result.message}`);
      }
    } catch (error) {
      console.error('Error during bulk upload:', error);

      // Handle unexpected errors
      const updatedData = bulkData.map((row) => {
        if (row.status !== 'pending') return row;
        return {
          ...row,
          status: 'failed',
          errors: [...(row.errors || []), 'Unexpected error occurred'],
        };
      });

      setBulkData(updatedData);
      setUploadResults({
        total: validRows.length,
        success: 0,
        failed: validRows.length,
      });

      alert('An unexpected error occurred during bulk upload');
    } finally {
      setIsBulkSubmitting(false);
    }
  };

  const handleCloseBulkModal = () => {
    setShowBulkUpload(false);
    setBulkData([]);
    setUploadFile(null);
    setUploadResults(null);
  };

  const getRowStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'error':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const filteredStudents = students.filter((student) => {
    const matchesSearch =
      (student.name && student.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (student.email && student.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (student.uid && student.uid.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'All' || student.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const pendingInvitations = invitations?.data?.filter((invitation) => {
    // Only show pending invitations for student/member roles
    const isStudentRole = invitation.role === 'org:member' || invitation.role === 'member';
    // Only show pending invitations for people who are NOT already active members
    const isAlreadyMember = students.some(student =>
      student.email.toLowerCase() === invitation.emailAddress.toLowerCase()
    );
    return invitation.status === 'pending' && isStudentRole && !isAlreadyMember;
  }) || [];

  return (
    <div className="bg-background p-6 h-screen flex flex-col overflow-hidden">
      <div>
        <div className="md:flex space-y-4 items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-foreground">All Students</h1>
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  Bulk upload
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setShowBulkUpload(true)}>Upload CSV</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowBulkUpload(true)}>Upload Excel</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button className="gap-2" onClick={() => setShowAddStudent(true)}>
              <Plus className="h-4 w-4" />
              Add student
            </Button>
          </div>
        </div>

        <div className="md:flex space-y-4 items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search for a student"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
                disabled={loading}
              />
            </div>
            <span className="text-sm text-muted-foreground">
              {loading ? 'Loading...' : `${filteredStudents.length} students`}
              {pendingInvitations.length > 0 && (
                <span className="ml-2 text-blue-600">• {pendingInvitations.length} pending invitations</span>
              )}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  Status: {statusFilter}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setStatusFilter('All')}>All</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('Active')}>Active</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('Inactive')}>Inactive</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('Suspended')}>Suspended</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem>All Grades</DropdownMenuItem>
                <DropdownMenuItem>Grade A</DropdownMenuItem>
                <DropdownMenuItem>Grade B</DropdownMenuItem>
                <DropdownMenuItem>Grade C</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Table with Fixed Layout */}
      <div className="flex flex-col flex-grow overflow-hidden rounded-md border">
        {/* Table Header */}
        <div className="flex bg-muted/40 hover:bg-muted/50 shrink-0 border-b">
          <div className="flex items-center px-4 py-3 w-12">
            <Checkbox
              className="rounded"
              checked={selectedStudents.length === filteredStudents.length && filteredStudents.length > 0}
              onCheckedChange={handleSelectAll}
              disabled={loading}
            />
          </div>
          <div className="flex items-center px-4 py-3 flex-1 min-w-0 text-sm font-medium">Student</div>
          <div className="flex items-center px-4 py-3 w-32 text-sm font-medium">UID</div>
          <div className="flex items-center px-4 py-3 flex-1 min-w-0 text-sm font-medium">Email</div>
          <div className="flex items-center px-4 py-3 w-20 text-sm font-medium">Grade</div>
          <div className="flex items-center px-4 py-3 w-24 text-sm font-medium">Status</div>
          <div className="flex items-center px-4 py-3 w-16"></div>
        </div>

        {/* Table Body */}
        <ScrollArea className="flex-grow">
          <div className="divide-y divide-border">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading students...</span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12">
                <span className="text-red-500">{error}</span>
              </div>
            ) : filteredStudents.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <span className="text-muted-foreground">No students found</span>
              </div>
            ) : (
              <>
                {pendingInvitations.length > 0 && (
                  <>
                    <div className="px-4 py-2 bg-blue-50 border-b">
                      <h3 className="text-sm font-medium text-blue-900">
                        Pending Invitations ({pendingInvitations.length})
                      </h3>
                    </div>
                    {pendingInvitations.map((invitation, idx) => (
                      <div
                        key={invitation.id}
                        className={`flex items-center hover:bg-muted/50 transition-colors ${
                          idx % 2 !== 0 ? 'bg-muted/20' : ''
                        }`}>
                        <div className="flex items-center px-4 py-4 w-12"></div>
                        <div className="flex items-center px-4 py-4 flex-1 min-w-0">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                              <User className="h-4 w-4 text-blue-600" />
                            </div>
                            <span className="font-medium truncate">{invitation.emailAddress}</span>
                          </div>
                        </div>
                        <div className="flex items-center px-4 py-4 w-32 text-muted-foreground font-mono text-sm">PENDING</div>
                        <div className="flex items-center px-4 py-4 flex-1 min-w-0 text-muted-foreground">
                          <span className="truncate">{invitation.emailAddress}</span>
                        </div>
                        <div className="flex items-center px-4 py-4 w-20">
                          <Badge variant="secondary" className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-xs">
                            N/A
                          </Badge>
                        </div>
                        <div className="flex items-center px-4 py-4 w-24">
                          <Badge variant="secondary" className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-xs">
                            Invited
                          </Badge>
                        </div>
                        <div className="flex items-center px-4 py-4 w-16">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem>Resend invitation</DropdownMenuItem>
                              <DropdownMenuItem className="text-red-500">Cancel invitation</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {students.length > 0 && (
                  <>
                    <div className="px-4 py-2 bg-gray-50 border-b">
                      <h3 className="text-sm font-medium text-gray-900">Active Students ({filteredStudents.length})</h3>
                    </div>
                    {filteredStudents.map((student, idx) => (
              <div
                key={student.uid}
                className={`flex items-center hover:bg-muted/50 transition-colors ${
                  idx % 2 !== 0 ? 'bg-muted/20' : ''
                }`}>
                <div className="flex items-center px-4 py-4 w-12">
                  <Checkbox
                    className="rounded"
                    checked={selectedStudents.includes(student.uid)}
                    onCheckedChange={() => handleStudentSelection(student.uid)}
                  />
                </div>
                <div className="flex items-center px-4 py-4 flex-1 min-w-0">
                  <div className="flex items-center gap-3 min-w-0">
                    {student.avatar ? (
                      <img
                        src={student.avatar}
                        alt={student.name}
                        className="w-8 h-8 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <User className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    <span className="font-medium truncate">{student.name}</span>
                  </div>
                </div>
                <div className="flex items-center px-4 py-4 w-32 text-muted-foreground font-mono text-sm">
                  {student.uid}
                </div>
                <div className="flex items-center px-4 py-4 flex-1 min-w-0 text-muted-foreground">
                  <span className="truncate">{student.email}</span>
                </div>
                <div className="flex items-center px-4 py-4 w-20">
                  <Badge variant="secondary" className={`${getGradeBadge(student.grade)} text-xs`}>
                    {student.grade || 'N/A'}
                  </Badge>
                </div>
                <div className="flex items-center px-4 py-4 w-24">
                  <Badge variant="secondary" className={`${getStatusBadge(student.status)} text-xs`}>
                    {student.status}
                  </Badge>
                </div>
                <div className="flex items-center px-4 py-4 w-16">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem>View profile</DropdownMenuItem>
                      <DropdownMenuItem>Edit student</DropdownMenuItem>
                      <div className="border-t my-1" />
                      <DropdownMenuItem>Reset password</DropdownMenuItem>
                      <DropdownMenuItem>Send invitation</DropdownMenuItem>
                      <div className="border-t my-1" />
                      <DropdownMenuItem>Transfer class</DropdownMenuItem>
                      <div className="border-t my-1" />
                      <DropdownMenuItem className="text-red-500">
                        {student.status === 'Suspended' ? 'Remove student' : 'Suspend student'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
                  </>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Add Student Modal */}
      <Dialog open={showAddStudent} onOpenChange={setShowAddStudent}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add New Student</DialogTitle>
            <DialogDescription>
              Enter the student's email and UID to send them an invitation to join the organization.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddStudent} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                placeholder="student@example.com"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={formErrors.email ? 'border-red-500' : ''}
                required
              />
              {formErrors.email && <p className="text-sm text-red-500">{formErrors.email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="uid">Student UID *</Label>
              <Input
                id="uid"
                type="text"
                placeholder="ST006"
                value={formData.uid}
                onChange={(e) => handleInputChange('uid', e.target.value.toUpperCase())}
                className={formErrors.uid ? 'border-red-500' : ''}
                maxLength={10}
                required
              />
              {formErrors.uid && <p className="text-sm text-red-500">{formErrors.uid}</p>}
              <p className="text-xs text-muted-foreground">
                UID must be unique and contain only uppercase letters and numbers
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCloseModal} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending invitation...' : 'Send Invitation'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Modal */}
      <Dialog open={showBulkUpload} onOpenChange={setShowBulkUpload}>
        <DialogContent className="sm:max-w-[800px] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Bulk Upload Students</DialogTitle>
            <DialogDescription>
              Download the template, fill it with student data, and upload to invite multiple students at once.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex flex-col space-y-4">
            {!uploadFile && (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Button variant="outline" onClick={downloadTemplate} className="gap-2">
                    <Download className="h-4 w-4" />
                    Download Template
                  </Button>
                  <p className="text-sm text-muted-foreground">Download and fill the CSV template with student data</p>
                </div>

                <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                  <input type="file" accept=".csv" onChange={handleFileUpload} className="hidden" id="bulk-upload" />
                  <label htmlFor="bulk-upload" className="cursor-pointer">
                    <div className="space-y-2">
                      <div className="mx-auto w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                        <Plus className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium">Click to upload your CSV file</p>
                        <p className="text-sm text-muted-foreground">CSV files up to 10MB</p>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {uploadFile && bulkData.length > 0 && (
              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-medium">Preview: {uploadFile.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {bulkData.length} entries found • {bulkData.filter((r) => r.status === 'pending').length} valid •{' '}
                      {bulkData.filter((r) => r.status === 'error').length} errors
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setUploadFile(null);
                      setBulkData([]);
                      setUploadResults(null);
                    }}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {uploadResults && (
                  <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                    <h4 className="font-medium mb-2">Upload Results</h4>
                    <div className="flex gap-4 text-sm">
                      <span className="text-green-600">✓ {uploadResults.success} successful</span>
                      <span className="text-red-600">✗ {uploadResults.failed} failed</span>
                      <span className="text-muted-foreground">Total: {uploadResults.total}</span>
                    </div>
                  </div>
                )}

                <div className="flex-1 min-h-0 border rounded-md">
                  <div className="flex bg-muted/40 border-b text-sm font-medium">
                    <div className="px-3 py-2 w-8">#</div>
                    <div className="px-3 py-2 flex-1">Email</div>
                    <div className="px-3 py-2 w-24">UID</div>
                    <div className="px-3 py-2 w-24">Status</div>
                    <div className="px-3 py-2 flex-1">Errors</div>
                  </div>

                  <ScrollArea className="h-64">
                    <div className="divide-y">
                      {bulkData.map((row, index) => (
                        <div key={index} className="flex items-center text-sm">
                          <div className="px-3 py-2 w-8 text-muted-foreground">{row.rowIndex}</div>
                          <div className="px-3 py-2 flex-1 truncate">{row.email}</div>
                          <div className="px-3 py-2 w-24 font-mono text-xs">{row.uid}</div>
                          <div className="px-3 py-2 w-24">
                            <Badge variant="secondary" className={`${getRowStatusBadge(row.status)} text-xs`}>
                              {row.status}
                            </Badge>
                          </div>
                          <div className="px-3 py-2 flex-1 text-xs text-red-600">{row.errors?.join(', ')}</div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 border-t pt-4">
            <Button variant="outline" onClick={handleCloseBulkModal} disabled={isBulkSubmitting}>
              {uploadResults ? 'Close' : 'Cancel'}
            </Button>

            {bulkData.length > 0 && !uploadResults && (
              <Button
                onClick={handleBulkSubmit}
                disabled={isBulkSubmitting || bulkData.filter((r) => r.status === 'pending').length === 0}>
                {isBulkSubmitting
                  ? 'Uploading...'
                  : `Upload ${bulkData.filter((r) => r.status === 'pending').length} Students`}
              </Button>
            )}

            {uploadResults && uploadResults.failed > 0 && (
              <Button
                onClick={() => {
                  // Filter out successful entries and reset failed ones to retry
                  const retryData = bulkData
                    .filter((row) => row.status === 'failed')
                    .map((row) => ({
                      ...row,
                      status: 'pending',
                      errors: row.errors.filter((e) => !e.includes('failed to send')),
                    }));
                  setBulkData(retryData);
                  setUploadResults(null);
                }}>
                Retry Failed ({uploadResults.failed})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
